import { FC, useMemo, useState } from 'react';
import LeadSelect from '../LeadSelect';
import LeadForm from '../LeadForm';
import { LeadFormProps } from '../LeadForm/LeadForm.component';
import useLead from 'pages/Lead/hooks/useLead';
import { convertLeadApiResponseToLead, isLeadPersonal } from 'utils/lead';
import { Lead, LeadApiResponse, LeadStatus } from 'models/Lead';
import ConfirmedLeadInfo from '../ConfirmedLeadInfo';
import { DenyLeadButton } from './components';
import { useFindLeads } from 'pages/Lead/hooks';
import CreateListingLeadForm from 'pages/Campaign/components/Leads/components/CreateListingLead/CreateListingLeadForm/CreateListingLeadForm';
import { useTanstackMutation } from 'hooks';
import { useQueryClient } from '@tanstack/react-query';
import { API_PATHS } from 'api';

import './LeadEditor.styles.scss';

type LeadEditorProps = Omit<LeadFormProps, 'lead'> & {
  id: string;
  lead?: Lead;
  isPersonal: boolean;
};

const LeadEditor: FC<LeadEditorProps> = (props) => {
  const { id, lead: intitialLead, isPersonal, ...rest } = props;

  const [manualLeadCreation, setManualLeadCreation] = useState(false);

  const queryClient = useQueryClient();

  const { data } = useLead(id, isPersonal);

  const lead = useMemo(
    () => (data ? convertLeadApiResponseToLead(data) : intitialLead),
    [data, intitialLead],
  );

  const {
    data: leads,
    refetch,
    isLoading: loadingLeadsInProgress,
    error,
    isError,
    isFetched,
  } = useFindLeads(lead?._id);
  const isPersonalLead = isLeadPersonal(lead);
  const hasLead = !isPersonalLead && !!(lead as Lead)?.contact;

  const {
    mutate: createLeadListingMutation,
    isPending: isPendingLeadListingCreation,
    isSuccess,
  } = useTanstackMutation({
    onSuccess: (response: LeadApiResponse) => {
      queryClient.setQueryData(['lead', { id: lead._id }], (oldData: any) => {
        if (!oldData) return { lead: response.lead };

        return {
          ...oldData,
          lead: response.lead,
        };
      });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['tabs'] });
      setManualLeadCreation(false);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const couldNotFindLeads = isFetched && !error && leads && leads.length === 0;
  const showLeadForm =
    (hasLead || isPersonalLead || isLeadPersonal(lead)) && !manualLeadCreation;
  const leadStatus = isPersonalLead
    ? lead?.leadStatus
    : lead?.contact?.leadStatus;

  if (!lead) return;

  return (
    <div className="lead-editor">
      {leadStatus === LeadStatus.CONFIRMED ? (
        <ConfirmedLeadInfo lead={lead} />
      ) : (
        <>
          {!isPersonalLead && (
            <LeadSelect
              leadOptions={leads}
              error={isError ? error : null}
              lead={lead}
              hasLead={hasLead}
              loadingLeadsInProgress={loadingLeadsInProgress}
              onFetchLeads={() => {
                setManualLeadCreation(false);
                refetch();
              }}
              onCreateManualLead={() => setManualLeadCreation(true)}
              onLeadSelect={() => setManualLeadCreation(false)}
            />
          )}
          {couldNotFindLeads && !isSuccess && (
            <p className="lead-editor__no-leads">
              Could not find leads. Use form instead.
            </p>
          )}
          {!lead?.isArchived && (
            <DenyLeadButton
              leadId={id}
              onSuccess={rest.onSuccess}
              className="lead-editor__deny-lead"
            />
          )}
          {showLeadForm && <LeadForm lead={lead} {...rest} />}
          {manualLeadCreation && (
            <CreateListingLeadForm
              submitInProgress={isPendingLeadListingCreation}
              onSubmit={(data) =>
                createLeadListingMutation({
                  method: 'post',
                  path: API_PATHS.listings.createListings(lead._id),
                  body: data,
                })
              }
            />
          )}
        </>
      )}
    </div>
  );
};

export default LeadEditor;
